/* global __APP_VERSION__ */

import ReactDOM from 'react-dom/client'
import { InjectionProvider } from '@contexts/InjectionContext.tsx'
import { QueryProvider } from '@contexts/QueryProvider.tsx'
import { InversionContainer } from '@controller/container.ts'
import { LifeCycle } from '@controller/lifeCycle.ts'
import isPropValid from '@emotion/is-prop-valid'
import { ILogger } from '@infra/logger/ILogger'
import { LogLevel } from '@infra/logger/LogLevels.ts'
import { DarkTheme, ThemeProvider } from '@nexds/web'
import { App } from '@presentation/App.tsx'
import { GlobalStyle } from '@presentation/global.styles.tsx'
import * as Sentry from '@sentry/react'
import { StyleSheetManager } from 'styled-components'
import { registerSW } from 'virtual:pwa-register'

import { BootstrapErrorDefault, BootstrapErrors } from './constants/BootstrapErrors'
import { InjectionTokens } from './controller/tokens'
import { AppErrorBoundary } from './presentation/modules/Base/components/AppErrorBoundary/AppErrorBoundary'
import { BootstrapErrorScreen } from './presentation/modules/Base/components/BootstrapError/BootstrapError'
import { SplashScreen } from './presentation/modules/Base/components/SplashScreen/SplashScreen'

import 'reflect-metadata'

// A helper to conditionally forward props
function shouldForwardProp(propName: string, target: any) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName)
  }
  // For other elements, forward all props
  return true
}

function reloadApp() {
  window.location.reload()
}

/**
 * Registra o service worker, DEPOIS do app montar.
 *
 * O bloco anterior aqui era código morto. Com `registerType: 'autoUpdate'` (ver `vite.config.ts`), o
 * template do `vite-plugin-pwa` só executa o branch `if (auto)`, e nesse branch **`onNeedRefresh`
 * nunca é chamado** — quem recarrega é o listener interno de `activated`. Portanto o
 * `newVersionAvailable` era sempre `false`, e a splash de update, o watchdog de 7,5 s e o listener de
 * `controllerchange` nunca rodavam. Verificado em
 * `node_modules/vite-plugin-pwa/dist/client/build/register.js`.
 *
 * Registrar após o mount não é ganho de tempo (o `updateSW` nunca bloqueou de fato: em dev o
 * `registerSW` é um no-op e em build ele só aguarda um dynamic import de ~6 KB). É para evitar que um
 * deploy no meio do boot dispare o `window.location.reload()` do listener de `activated` enquanto a
 * splash está na tela.
 */
function registerServiceWorkerAfterMount(): void {
  const run = () => registerSW({ immediate: true })

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => run(), { timeout: 2000 })
    return
  }

  setTimeout(run, 0)
}

const bootstrap = async () => {
  console.log(`[Bootstrap] Bootstrapping app version ${__APP_VERSION__}, mode: ${import.meta.env.MODE}`)

  Sentry.init({
    dsn: 'https://f53c62663e7c414999ad4bd73b39e7af@sentry.nexatlas.com/19',
    integrations: [Sentry.browserTracingIntegration()],
    release: __APP_VERSION__,
    tracesSampleRate: 0.1
  })

  const loadingRoot = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
  loadingRoot.render(
    <>
      {/* // @ts-ignore */}
      <ThemeProvider theme={DarkTheme}>
        <GlobalStyle />
        <SplashScreen />
      </ThemeProvider>
    </>
  )

  // Initialize dependency injection and lifecycle components.
  const container = InversionContainer.getInstance().getContainer()
  const logger = container.get<ILogger>(InjectionTokens.Logger)
  logger.setLogLevel(import.meta.env.MODE === 'production' ? LogLevel.Warn : LogLevel.Debug)

  const lifeCycle = new LifeCycle(container)
  const initializationResult = await lifeCycle.initializeComponents()

  if (initializationResult.isFailure) {
    logger.error('Bootstrap', initializationResult.error)
    let errorHandler =
      BootstrapErrors.find((error) => error.code === initializationResult.error) || BootstrapErrorDefault

    loadingRoot.unmount()
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <>
        {/* // @ts-ignore */}
        <ThemeProvider theme={DarkTheme}>
          <GlobalStyle />
          <BootstrapErrorScreen error={errorHandler} />
        </ThemeProvider>
      </>
    )
    return
  }

  loadingRoot.unmount()

  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <InjectionProvider container={container}>
      {/* Só a árvore do app real recebe o QueryProvider: a splash de update de SW e o
          BootstrapErrorScreen não fazem query. */}
      <QueryProvider>
        <StyleSheetManager shouldForwardProp={shouldForwardProp}>
          {/* // @ts-ignore */}
          <ThemeProvider theme={DarkTheme}>
            <GlobalStyle />
            <AppErrorBoundary logger={logger} onAction={reloadApp}>
              <App />
            </AppErrorBoundary>
          </ThemeProvider>
        </StyleSheetManager>
      </QueryProvider>
    </InjectionProvider>
  )

  // Servicos de sync/prefetch sobem depois do App montado, fora do caminho critico do boot.
  lifeCycle.startBackgroundServices()
  registerServiceWorkerAfterMount()
}

bootstrap()
